import React from 'react';
import { Box, Typography, IconButton, CircularProgress, Chip } from '@mui/material';
import { TextInput } from '@dooglie/ui';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { DiscoveryButton, DiscoveryIntent } from './types';
import type { AttachmentStatus, FileUploadView } from '../chat/FileUploadView';
import { tourAnchor } from '../tour/anchors';

type FileUpload = FileUploadView;

const CHIP_STATUS_LABEL: Record<AttachmentStatus, string> = {
  uploading: 'Uploading…',
  uploaded: 'Attached',
  failed: 'Failed',
};

/** The line beside the chips: what is attached, or why something is not. */
function attachSummary(files: FileUpload): string {
  if (files.uploadingFiles) return 'Uploading…';
  if (files.uploadError) return files.uploadError;
  const n = files.fileIds.length;
  return `${n} file${n === 1 ? '' : 's'} attached`;
}

/** The composer's box: a soft field that lifts to white with a cobalt ring on focus. */
const COMPOSER_SX = {
  px: 2,
  // 1px less than the old outlined field's 10px: the border now takes it
  // (an outline's fieldset took no room).
  py: 1.125,
  minHeight: 42,
  fontSize: 'var(--medium-font-size)',
  lineHeight: 1.5,
  color: 'var(--n800)',
  bgcolor: 'var(--n50)',
  border: '1px solid var(--n100)',
  borderRadius: 'var(--r-lg)',
  '&:hover': { borderColor: 'var(--brand-cobalt-lite)' },
  '&.Mui-focused': {
    bgcolor: 'var(--white)',
    borderColor: 'var(--brand-cobalt)',
    boxShadow: 'var(--ring-cobalt)',
  },
  '& .MuiInputBase-input::placeholder': { color: 'var(--n400)', opacity: 1 },
} as const;

type MessageInputProps = {
  input: string;
  placeholder: string;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  loading: boolean;
  discoveryButtons: DiscoveryButton[];
  files: FileUpload;
  onValueChange: (text: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  onButtonClick: (intent: DiscoveryIntent) => void;
  /** Drawn above the text box, inside the composer (the tour's hint chips). */
  hints?: React.ReactNode;
};

const MessageInput: React.FC<MessageInputProps> = ({
  input,
  placeholder,
  textareaRef,
  loading,
  discoveryButtons,
  files,
  onValueChange,
  onFocus,
  onBlur,
  onKeyDown,
  onSend,
  onButtonClick,
  hints,
}) => {
  return (
    <Box component="footer" className="dg-chat-composer" {...tourAnchor('chat.input')}>
      {files.attachments.length > 0 && (
        <Box className="dg-chat-attach-bar">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <AttachFileIcon
              sx={{ color: 'var(--brand-cobalt)', fontSize: '20px' }}
            />
            <Typography
              variant="body2"
              role={files.uploadError ? 'alert' : undefined}
              sx={{
                color: files.uploadError ? 'error.main' : 'var(--brand-cobalt)',
                fontWeight: 500,
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
              }}
            >
              {attachSummary(files)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                flexWrap: 'wrap',
                ml: 1,
                flex: 1,
                overflow: 'hidden',
              }}
            >
              {files.attachments.map((attachment, index) => {
                const failed = attachment.status === 'failed';
                return (
                  <Chip
                    key={index}
                    label={
                      attachment.status === 'uploaded'
                        ? attachment.name
                        : `${attachment.name} · ${CHIP_STATUS_LABEL[attachment.status]}`
                    }
                    icon={
                      failed ? (
                        <ErrorOutlineIcon sx={{ fontSize: '14px' }} />
                      ) : undefined
                    }
                    size="small"
                    data-status={attachment.status}
                    sx={{
                      backgroundColor: failed
                        ? 'rgba(211, 47, 47, 0.08)'
                        : 'var(--brand-cobalt-ghost)',
                      color: failed ? 'error.main' : 'var(--brand-cobalt)',
                      fontSize: '0.7rem',
                      height: '20px',
                      '& .MuiChip-icon': { color: 'error.main', ml: '4px' },
                      '& .MuiChip-label': {
                        padding: '0 8px',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={files.clearFiles}
            aria-label="Remove attachments"
            sx={{
              color: 'var(--n600)',
              '&:hover': {
                bgcolor: 'var(--n100)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {discoveryButtons.length > 0 && (
        <div className="dg-chat-composer-actions">
          {discoveryButtons.map((button) => (
            <button
              key={button.intent}
              type="button"
              className="dg-chat-prompt-btn"
              onClick={() => onButtonClick(button.intent)}
              disabled={loading || files.uploadingFiles}
            >
              {button.label}
            </button>
          ))}
        </div>
      )}

      {hints}

      <div className="dg-chat-input-row">
        <TextInput
          className="dg-chat-input-field"
          field="chat.composer"
          hint="A message to Dooglie"
          multiline
          fullWidth
          minRows={1}
          maxRows={6}
          inputRef={textareaRef}
          value={input}
          onValueChange={onValueChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          sx={COMPOSER_SX}
        />
        <input
          ref={files.fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={files.handleFileSelect}
        />
        <div className="dg-chat-icon-actions">
          <button
            type="button"
            className="dg-chat-icon-btn"
            onClick={files.handleFileButtonClick}
            disabled={files.uploadingFiles}
            aria-label="Attach file"
            {...tourAnchor('chat.attach')}
          >
            {files.uploadingFiles ? (
              <CircularProgress size={20} sx={{ color: 'var(--brand-cobalt)' }} />
            ) : (
              <AttachFileIcon />
            )}
          </button>
          <button
            type="button"
            className="dg-chat-icon-btn"
            onClick={onSend}
            disabled={files.uploadingFiles}
            aria-label="Send message"
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </Box>
  );
};

export default MessageInput;
